import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader2, Mic } from 'lucide-react';
import { transcribeAudio } from '../services/speechService';
import { logger } from '../../lib/logger';

interface ChatInputProps {
  onSend: (message: string) => void;
  isLoading: boolean;
  placeholder?: string;
}

// Synthesizer animation bars
function SynthesizerAnimation() {
  return (
    <div className="flex items-center justify-center gap-1 h-12">
      {[...Array(12)].map((_, i) => (
        <div
          key={i}
          className="w-1 bg-primary rounded-full animate-synthesizer"
          style={{
            animationDelay: `${i * 0.1}s`,
            height: '100%',
          }}
        />
      ))}
    </div>
  );
}

export function ChatInput({
  onSend,
  isLoading,
  placeholder = 'Nachricht eingeben...',
}: ChatInputProps) {
  const [input, setInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [input]);

  const handleSubmit = () => {
    if (input.trim() && !isLoading && !isTranscribing) {
      onSend(input.trim());
      setInput('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const startRecording = useCallback(async () => {
    setRecordingError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      });

      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());

        if (audioChunksRef.current.length === 0) {
          setRecordingError('Keine Audio-Daten aufgenommen');
          return;
        }

        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

        setIsTranscribing(true);
        try {
          const text = await transcribeAudio(audioBlob);
          if (text.trim()) {
            // Send immediately after transcription
            onSend(text.trim());
          }
        } catch (error) {
          logger.error('Transcription error:', error);
          setRecordingError('Transkription fehlgeschlagen');
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      logger.error('Error starting recording:', error);
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        setRecordingError('Mikrofon-Zugriff verweigert');
      } else {
        setRecordingError('Mikrofon nicht verfügbar');
      }
    }
  }, [onSend]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const isDisabled = isLoading || isTranscribing;

  // Recording mode UI
  if (isRecording) {
    return (
      <div data-tour="ai-input" className="border-t border-gray-100 p-4 bg-white">
        <div className="flex flex-col items-center gap-4">
          {/* Synthesizer animation */}
          <div className="w-full px-4">
            <SynthesizerAnimation />
          </div>

          {/* Recording indicator text */}
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            Aufnahme läuft...
          </p>

          {/* Big green finish button */}
          <button
            onClick={stopRecording}
            className="w-full py-4 rounded-xl bg-green-500 hover:bg-green-600
                     text-white font-semibold text-lg
                     transition-all shadow-md hover:shadow-lg
                     flex items-center justify-center gap-2"
          >
            ✓ Fertig
          </button>
        </div>
      </div>
    );
  }

  // Transcribing mode UI
  if (isTranscribing) {
    return (
      <div data-tour="ai-input" className="border-t border-gray-100 p-4 bg-white">
        <div className="flex flex-col items-center gap-4 py-4">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
          <p className="text-sm text-gray-500">Transkribiere...</p>
        </div>
      </div>
    );
  }

  // Normal input mode
  return (
    <div data-tour="ai-input" className="border-t border-gray-100 p-4 bg-white">
      <div className="flex items-end gap-2">
        {/* Microphone button */}
        <button
          onClick={startRecording}
          disabled={isDisabled}
          className="w-10 h-10 rounded-xl bg-gray-100 text-gray-600
                   hover:bg-gray-200 flex items-center justify-center transition-all
                   disabled:opacity-50 disabled:cursor-not-allowed"
          title="Sprachaufnahme starten"
        >
          <Mic className="w-5 h-5" />
        </button>

        {/* Text input */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isDisabled}
            rows={1}
            className="w-full resize-none rounded-xl border border-gray-200 px-4 py-3 text-sm
                     focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none
                     disabled:bg-gray-50 disabled:cursor-not-allowed
                     placeholder:text-gray-400 transition-colors"
          />
        </div>

        {/* Send button */}
        <button
          onClick={handleSubmit}
          disabled={!input.trim() || isDisabled}
          className="w-10 h-10 rounded-xl bg-gradient-primary
                   text-white flex items-center justify-center
                   hover:bg-primary-dark
                   disabled:bg-gray-400 disabled:cursor-not-allowed
                   transition-all shadow-sm hover:shadow-md"
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Error message */}
      {recordingError && (
        <p className="text-xs text-red-500 mt-2 text-center">
          {recordingError}
        </p>
      )}

      {/* Help text */}
      <p className="text-xs text-gray-400 mt-2 text-center">
        Enter zum Senden • Mikrofon für Spracheingabe
      </p>
    </div>
  );
}
